<template>
  <LmDialog
    v-if="modalShow && !(showProductForm || showProviderForm)"
    v-model="modalShow"
    title="Create Subscription Period"
  >
    <template #toolbar-actions>
      <q-btn :disable="created" size="sm" color="tertiary" label="Save" @click.prevent="sendData" />
    </template>

    <template #content>
      <q-splitter :value="splitterModel" :disable="true" style="overflow: auto">
        <template #before>
          <q-tabs v-model="activeTab" vertical class="text-teal">
            <q-tab name="basic" label="Basic" />
            <q-tab v-if="!subscriptionPeriod" name="metaData" label="Metadata" />
          </q-tabs>
        </template>

        <template #after>
          <q-tab-panels v-model="activeTab" keep-alive animated transition-prev="scale" transition-next="scale">
            <q-tab-panel name="basic">
              <q-card-section :style="{ height: '600px' }">
                <div class="row q-col-gutter-md q-mb-xs items-start">
                  <div class="col-xs-12">
                    <q-checkbox
                      v-model="formData.one_time_purchase"
                      :disable="disable('one_time_purchase')"
                      class="lm-checkbox"
                      color="tertiary"
                      label="One time purchase"
                    />
                  </div>

                  <div class="col-md-3">
                    <div class="col-auto">
                      <enhanced-q-select
                        id="product"
                        v-model="$v.formData.product_id.$model"
                        :error="$v.formData.product_id.$error"
                        :error-message="$v_displayMessage($v.formData.product_id)"
                        :class="{ disabled: haveProductId }"
                        :readonly="haveProductId || disable('product_id')"
                        :disable="disable('product_id')"
                        :options="productOptions"
                        label="Product *"
                        hide-bottom-space
                      />
                    </div>
                    <div class="col-auto q-pt-sm">
                      <LinkButton text="Create new product" @click="openProductForm" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <q-input
                      id="name_reference"
                      v-model="$v.formData.name_reference.$model"
                      :error="$v.formData.name_reference.$error"
                      :error-message="$v_displayMessage($v.formData.name_reference)"
                      label="Detail"
                      outlined
                    />
                  </div>

                  <div class="col-md-3">
                    <LmDatePicker
                      v-model="$v.formData.start_date.$model"
                      :error="$v.formData.start_date.$error"
                      :error-message="$v_displayMessage($v.formData.start_date)"
                      label="Start Date *"
                    />
                  </div>

                  <div v-if="!formData.one_time_purchase" class="col-md-3">
                    <div class="col-md-3">
                      <LmDatePicker
                        v-model="$v.formData.end_date.$model"
                        :error="$v.formData.end_date.$error"
                        :error-message="$v_displayMessage($v.formData.end_date)"
                        :options="isDateAfterStartDate"
                        label="End Date *"
                      />
                    </div>
                  </div>
                </div>

                <div class="row q-col-gutter-md q-mb-lg q-mt-xs">
                  <div class="col-md-3">
                    <enhanced-q-select
                      id="owner"
                      v-model="$v.formData.owner_id.$model"
                      :error="$v.formData.owner_id.$error"
                      :error-message="$v_displayMessage($v.formData.owner_id)"
                      :disable="disable('owner_id')"
                      :options="ownerOptions"
                      label="Owner *"
                    />
                  </div>

                  <div class="col-md-3">
                    <div class="col-auto">
                      <enhanced-q-select
                        id="vendor"
                        v-model="formData.vendor_id"
                        :disable="disable('vendor_id')"
                        :options="vendorOptions"
                        label="Vendor"
                      />
                    </div>
                    <div class="col-auto q-pt-sm">
                      <LinkButton text="Create new vendor" @click="openProviderForm" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <LmDatePicker
                      ref="cancellation-deadline-picker"
                      v-model="$v.formData.cancellation_deadline.$model"
                      :error="$v.formData.cancellation_deadline.$error"
                      :error-message="$v_displayMessage($v.formData.cancellation_deadline)"
                      :options="isDateBeforeStartDate"
                      label="Decision Deadline"
                    />
                  </div>

                  <div class="col-md-3">
                    <q-select
                      v-model="$v.formData.financial_period_id.$model"
                      :error="$v.formData.financial_period_id.$error"
                      :error-message="$v_displayMessage($v.formData.financial_period_id)"
                      :options="financialPeriodOptions"
                      emit-value
                      map-options
                      outlined
                      label="Financial period *"
                    />
                  </div>
                </div>

                <h6 class="q-mb-lg">Price</h6>

                <div class="price-inputs">
                  <div class="col-md-2">
                    <enhanced-q-select
                      id="currency"
                      v-model="$v.formData.currency.$model"
                      :error="$v.formData.currency.$error"
                      :error-message="$v_displayMessage($v.formData.currency)"
                      :disable="disable('currency')"
                      :options="currencies"
                      label="Original currency *"
                    />
                  </div>

                  <div id="forecasted_price" class="col-md-3">
                    <q-input
                      ref="forecastedPriceInput"
                      :value="forecastedPrice"
                      :hint="
                        `Use '${thousandsSeparator}' for thousands, '${decimalSeparator}' for
                               decimals`
                      "
                      type="text"
                      label="Forecasted price"
                      outlined
                      @blur="formatPriceForDisplay('forecastedPrice')"
                      @focus="selectContent"
                    />
                  </div>

                  <div id="price" class="col-md-2">
                    <q-input
                      ref="priceInput"
                      :value="price"
                      :hint="
                        `Use '${thousandsSeparator}' for thousands, '${decimalSeparator}' for
                               decimals`
                      "
                      type="text"
                      label="Original price"
                      outlined
                      @blur="formatPriceForDisplay('price')"
                      @focus="selectContent"
                    />
                  </div>

                  <div class="col-md-2">
                    <q-input
                      id="normalized_currency"
                      :value="settings.currency"
                      :disable="disable('normalized_currency')"
                      type="text"
                      stack-label
                      label="Local currency"
                      required
                      readonly
                      outlined
                    />
                  </div>

                  <div id="normalized_price" class="col-md-3">
                    <q-input
                      ref="normalizedPriceInput"
                      :value="normalizedPrice"
                      :disable="disable('normalized_price')"
                      :hint="
                        `Use '${thousandsSeparator}' for thousands, '${decimalSeparator}' for
                            decimals`
                      "
                      label="Local price"
                      type="text"
                      outlined
                      @blur="formatPriceForDisplay('normalizedPrice')"
                      @focus="selectContent"
                    />
                  </div>
                </div>

                <div class="row q-col-gutter-md gutter-x-md">
                  <div class="col-md-12">
                    <h6>Workflow settings</h6>
                  </div>

                  <div class="tasks-wrapper">
                    <template v-if="!tasks">
                      <div class="loader">
                        <q-spinner color="primary" size="2.5em" />
                      </div>
                    </template>
                    <MilestoneTasks v-else :tasks="tasks" has-dependencies @updated="tasks = $event" />
                  </div>
                </div>
              </q-card-section>
            </q-tab-panel>

            <q-tab-panel v-if="!subscriptionPeriod" name="metaData">
              <q-card-section :style="{ height: '600px' }">
                <ModalMetaData ref="metaData" :meta-data-fields="metaDataFields" />
              </q-card-section>
            </q-tab-panel>
          </q-tab-panels>
          <q-card-actions
            v-if="hasEndAction && showEndActions && result"
            align="right"
            class="row q-pa-lg q-mr-sm justify-end"
          >
            <q-btn label="Return to list" size="sm" color="positive" @click="closeModal" />
            <q-btn
              :to="{
                name: 'subscription',
                params: { id: result.subscription.id }
              }"
              color="tertiary"
              size="sm"
              class="on-right"
              label="Go to subscription page"
            />
          </q-card-actions>
        </template>
      </q-splitter>
    </template>
  </LmDialog>
  <form-create-product
    v-else-if="showProductForm"
    :showing="showProductForm"
    :product-name="productName"
    return-to-subscription
    context="index"
    @close="showProductForm = false"
    @create="setProductId"
  />
  <form-create-provider
    v-else-if="showProviderForm"
    :showing="showProviderForm"
    :provider-name="vendorName"
    stay-label="Return to Subscription Form"
    go-label="Go to Vendor Page"
    namespace="vendors"
    entity-name="vendor"
    @create="setVendorId"
    @close="showProviderForm = false"
  />
</template>

<script>
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import cleaveMixin from '@/mixins/cleave-mixin'
import { isDateValid } from '@/lib/validation-helpers'
import { getFinancialPeriod } from '@/utils/get-financial-period'
import { validations } from './validations'
// Libraries
import { mapGetters, mapMutations } from 'vuex'
import { date } from 'quasar'
import moment from 'moment'
import { cloneDeep, get } from 'lodash'
// Components
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import FormCreateProduct from '@components/forms/form-create-product/FormCreateProduct'
import FormCreateProvider from '@components/forms/form-create-provider/FormCreateProvider'
import LinkButton from '@/reusable/base-components/LinkButton'
import LmDatePicker from '@/reusable/LmDatePicker/LmDatePicker'
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import MilestoneTasks from '@/reusable/milestone-tasks/MilestoneTasks'
import ModalMetaData from '@/reusable/ModalMetaData'

export default {
  name: 'FormSubscriptionPeriod',

  components: {
    EnhancedQSelect,
    FormCreateProduct,
    FormCreateProvider,
    LinkButton,
    LmDatePicker,
    LmDialog,
    MilestoneTasks,
    ModalMetaData
  },

  mixins: [cleaveMixin, validations],

  props: {
    subscriptionId: [String, Number],
    subscriptionPeriod: Object,
    showing: Boolean,
    hasEndAction: Boolean,
    disableProperties: {
      type: Array,
      default: () => []
    },
    originalTasks: Array
  },

  data() {
    return {
      price: 0,
      normalizedPrice: 0,
      forecastedPrice: null,
      formData: {},
      created: false,
      product: null,
      showEndActions: false,
      result: null,
      showProductForm: false,
      showProviderForm: false,
      productName: null,
      vendorName: null,
      date: null,
      tasks: null,
      activeTab: 'basic',
      splitterModel: 10
    }
  },

  computed: {
    ...mapGetters('subscriptions/subscriptionPeriods', [
      'owners',
      'vendors',
      'products',
      'productId',
      'vendorId',
      'newSubscriptionPeriod',
      'vendorOptions',
      'ownerOptions',
      'productOptions',
      'metaDataFields',
      'financialPeriods'
    ]),
    modalShow: {
      get() {
        return this.showing
      },
      set(val) {
        !val && this.closeModal()
      }
    },
    haveProductId() {
      return this.productId !== null && typeof this.productId !== 'undefined'
    },
    publisherName() {
      if (!this.formData.product_id) return
      let product = this.products.find(product => product.id === this.formData.product_id)
      return get(product, 'publisher.name')
    },
    isOriginalCurrencySameAsLocalCurrency() {
      return this.formData.currency === this.settings.currency
    },
    financialPeriodOptions() {
      return this.financialPeriods.map(period => ({
        label: `${this.formatDateForLocale(period.start_date)} - ${this.formatDateForLocale(period.end_date)}`,
        value: period.id
      }))
    }
  },

  watch: {
    showing(newVal) {
      this.created = !newVal
    },
    originalTasks: {
      handler(val) {
        this.tasks = cloneDeep(val)
      },
      immediate: true
    },
    productId: 'productIdChanged',
    product(newVal) {
      if (!newVal) {
        return
      }
      this.formData = this.extractSubscriptionPeriodInformation(this.subscriptionPeriod)
      this.showEndActions = false
      this.result = null
      this.created = false
      this.formData.currency = get(newVal, 'publisher.currency', null)
      this.initPrices()
    },
    subscriptionPeriod(newVal) {
      if (newVal) {
        this.formData = this.extractLicenseInformation(newVal)
      }
    },
    'formData.start_date'(val) {
      if (!isDateValid.call(this, val) || !val) return
      const startDate = moment(val, this.settings.date_format)
      const cancellationDate = moment(this.formData.cancellation_deadline, this.settings.date_format)

      if (startDate.isBefore(cancellationDate)) this.formData.cancellation_deadline = null
      this.formData.financial_period_id = getFinancialPeriod(
        startDate,
        this.financialPeriods,
        this.settings.date_format
      )?.id
      this.formData.end_date = moment(val, this.settings.date_format)
        .add(1, 'year')
        .subtract(1, 'day')
        .format(this.settings.date_format)
    },
    showProductForm(newVal) {
      if (!newVal) {
        let formData = cloneDeep(this.newSubscriptionPeriod)
        formData.product_id = this.productId
        formData.currency = this.formData.currency
        formData.owner_id = this.formData.owner_id
        this.formData = formData
        this.setCleaveFields()
      }
    },
    showProviderForm(newVal) {
      if (!newVal) {
        let formData = cloneDeep(this.newSubscriptionPeriod)
        formData.vendor_id = this.vendorId
        this.formData = formData
        this.setCleaveFields()
      }
    },
    'formData.product_id': 'updateCurrencyFromProduct',
    'formData.vendor_id': 'updateCurrencyFromVendor',
    modalShow(val) {
      if (val && !(this.showProductForm || this.showProviderForm)) {
        this.setCleaveFields()
      } else if (!val) {
        this.activeTab = 'basic'
        this.$v.$reset()
        if (this.$refs.metaData) this.$refs.metaData.resetValidation()
      }
    },
    isOriginalCurrencySameAsLocalCurrency(currenciesAreTheSame) {
      if (currenciesAreTheSame) {
        this.price = this.normalizedPrice
      }
    }
  },

  created() {
    if (this.productId) {
      this.product = this.products.find(product => product.id === this.productId)
    }
    this.formData = this.extractSubscriptionPeriodInformation(this.subscriptionPeriod)
    this.created = false
    this.initPrices()
    if (this.subscriptionPeriod) {
      this.setCleaveFields()
      this.splitterModel = 0
    }
  },

  methods: {
    isDateBeforeStartDate(date) {
      return moment(date, 'YYYY/MM/DD').isBefore(
        moment(this.formData.start_date, this.settings.date_format).format('YYYY-MM-DD')
      )
    },
    isDateAfterStartDate(date) {
      if (!this.formData.start_date) return true
      return moment(date, 'YYYY/MM/DD').isAfter(
        moment(this.formData.start_date, this.settings.date_format).format('YYYY-MM-DD')
      )
    },
    ...mapMutations('subscription-show', ['addPeriod']),
    initPrices() {
      this.price = 0
      this.normalizedPrice = 0
      this.formatPriceForDisplay('price')
      this.formatPriceForDisplay('normalizedPrice')
    },
    extractSubscriptionPeriodInformation(subscriptionPeriod) {
      let startDate = new Date()
      let cancellation_deadline = null
      if (subscriptionPeriod) {
        startDate = date.addToDate(subscriptionPeriod.end_date, { days: 1 })
      }
      if (subscriptionPeriod?.cancellation_deadline) {
        cancellation_deadline = moment(subscriptionPeriod.cancellation_deadline, this.globalVarsAndFns.BEdateFormat)
          .add(1, 'year')
          .format(this.settings.date_format)
      }
      startDate = moment(startDate).format(this.settings.date_format)
      const endDate = moment(startDate, this.settings.date_format)
        .add(1, 'year')
        .subtract(1, 'day')
        .format(this.settings.date_format)

      const financialPeriodToPreselect = getFinancialPeriod(startDate, this.financialPeriods, this.settings.date_format)

      return new LmFormObject({
        name_reference: '',
        financial_period_id: financialPeriodToPreselect?.id,
        subscription_id: this.subscriptionId,
        vendor_id: get(subscriptionPeriod, 'vendor.id', null),
        currency: get(subscriptionPeriod, 'currency', this.currency),
        normalized_currency: this.settings.currency,
        one_time_purchase: get(subscriptionPeriod, 'subscriptionPeriod.one_time_purchase', false),
        start_date: startDate,
        end_date: endDate,
        price: null,
        normalized_price: null,
        forecasted_price: null,
        publisher_id: subscriptionPeriod
          ? subscriptionPeriod.publisher
            ? subscriptionPeriod.publisher.id
            : subscriptionPeriod.subscription.product.publisher.id
          : this.product
          ? this.product.publisher.id
          : null,
        product_id: subscriptionPeriod
          ? subscriptionPeriod.product
            ? subscriptionPeriod.product.id
            : subscriptionPeriod.subscription.product.id
          : this.productId,
        owner_id: subscriptionPeriod ? subscriptionPeriod.owner.id : this.product ? this.product.owner_id : null,
        cancellation_deadline
      })
    },
    updateCurrencyFromVendor(vendorId) {
      if (this.subscriptionPeriod) return
      if (!vendorId && this.formData.product_id) {
        this.updateCurrencyFromProduct(this.formData.product_id)
        return
      }
      const vendor = this.vendors.find(item => item.id === vendorId)
      if (vendor) this.formData.currency = vendor.currency
    },
    updateCurrencyFromProduct(productId) {
      const product = this.products.find(item => item.id === productId)
      if (this.subscriptionPeriod) return
      if (!this.formData.vendor_id && product) this.formData.currency = product.publisher.currency
      if (product && product.owner_id) this.formData.owner_id = product.owner_id
    },
    formatPriceForDisplay(field) {
      const formattedPriceNumber = this.formatInputNumber(this[field]) || null
      this[field] = formattedPriceNumber
      if (this.isOriginalCurrencySameAsLocalCurrency) {
        if (field === 'normalizedPrice') {
          this.price = formattedPriceNumber
        } else if (field === 'price') {
          this.normalizedPrice = formattedPriceNumber
        }
      }
      return this[field]
    },
    formatData() {
      this.$refs.priceInput.$emit('blur')
      this.$refs.normalizedPriceInput.$emit('blur')
      this.$refs.forecastedPriceInput.$emit('blur')
      let formData = cloneDeep(this.formData)
      let product = find(this.products, product => {
        return product.id === this.formData.product
      })
      if (!this.subscriptionPeriod) {
        if (this.$refs.metaData) {
          formData.meta_data_fields_by_category = this.$refs.metaData.getUpdatedMetaData()
        } else {
          formData.meta_data_fields_by_category = this.metaDataFields
        }
      }
      if (product) {
        formData.publisher_id = product.publisher_id
      }
      formData.task_applicability = this.tasks
      formData.price = this.price ? this.convertToNumber(this.price) : null
      formData.normalized_price = this.normalizedPrice ? this.convertToNumber(this.normalizedPrice) : null
      formData.forecasted_price = this.forecastedPrice ? this.convertToNumber(this.forecastedPrice) : null
      if (this.formData.start_date) {
        formData.start_date = this.formatDateForPost(this.formData.start_date, this.settings.date_format)
      }
      if (this.formData.end_date) {
        formData.end_date = formData.one_time_purchase
          ? null
          : this.formatDateForPost(this.formData.end_date, this.settings.date_format)
      }
      if (this.formData.cancellation_deadline) {
        formData.cancellation_deadline = this.formatDateForPost(
          this.formData.cancellation_deadline,
          this.settings.date_format
        )
      }
      if (this.subscriptionId) formData.subscription_id = this.subscriptionId
      delete formData.errors
      delete formData.originalData
      return formData
    },
    sendData() {
      if (!this.subscriptionPeriod) {
        if (!this.$v_validationPass()) return
      } else {
        this.$v.$touch()
        if (this.$v.$invalid) return
      }

      let params = {
        value: this.formatData()
      }
      this.$store.dispatch('subscriptions/subscriptionPeriods/create', params).then(result => {
        if (Object.keys(this.$store._modules.root._children).includes('subscription-show')) {
          this.addPeriod(result)
        }
        this.created = true
        if (!this.hasEndAction) {
          this.modalShow = false
          this.formData = this.extractSubscriptionPeriodInformation(this.subscriptionPeriod)
          this.initPrices()
          this.$emit('refresh', { id: result.id })
        }
        if (this.hasEndAction) {
          this.result = result
          this.showEndActions = true
        }
      })
    },
    selectContent(event) {
      event.target.select()
    },
    disable(key) {
      return this.disableProperties.includes(key)
    },
    closeModal() {
      this.$emit('close', this.created)
      this.created = false
      this.$store.dispatch('subscriptions/subscriptionPeriods/setProductId', null)
      this.$store.dispatch('subscriptions/subscriptionPeriods/setVendorId', null)
      this.$store.dispatch('products/setPublisherId', null)
      this.product = null
      this.formData = this.extractSubscriptionPeriodInformation(this.subscriptionPeriod)
      this.showEndActions = false
      this.initPrices()
    },
    saveCurrentState() {
      this.$store.dispatch('subscriptions/subscriptionPeriods/setNewSubscriptionPeriod', cloneDeep(this.formData))
    },
    openProviderForm() {
      this.saveCurrentState()
      this.showProviderForm = true
    },
    openProductForm() {
      this.saveCurrentState()
      this.showProductForm = true
    },
    async setProductId(product) {
      await this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
      this.$store.dispatch('subscriptions/subscriptionPeriods/setProductId', product.id)
    },
    setVendorId(vendor) {
      this.$store.dispatch('subscriptions/subscriptionPeriods/setVendorId', vendor.id)
      this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
    },
    setCleaveFields() {
      this.inputPriceFormat('#normalized_price .q-field__native', 2, ({ target: valueChanged }) => {
        this.normalizedPrice = valueChanged.value
        if (this.isOriginalCurrencySameAsLocalCurrency) {
          this.price = valueChanged.value
        }
      })
      this.inputPriceFormat('#price .q-field__native', 2, ({ target: valueChanged }) => {
        this.price = valueChanged.value
        if (this.isOriginalCurrencySameAsLocalCurrency) {
          this.normalizedPrice = valueChanged.value
        }
      })
      this.inputPriceFormat('#forecasted_price .q-field__native', 2, ({ target: valueChanged }) => {
        this.forecastedPrice = valueChanged.value
      })
    },

    async productIdChanged(newVal, oldVal) {
      const findProduct = id => this.products.find(product => product.id === id)

      if (!findProduct(newVal)) {
        await this.$store.dispatch('subscriptions/subscriptionPeriods/getCreateFormData')
      }

      if (newVal && newVal !== oldVal) {
        let product = this.products.find(product => {
          return product.id === newVal
        })
        if (newVal !== this.formData.product_id) {
          this.formData.product_id = newVal
        }
        this.product = product
      }
    }
  }
}
</script>
<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'

.q-card {
  width: 1200px;
  max-width: 1200px;
}

.tasks-wrapper {
  display grid
  width 100%
  padding 15px

  .loader {
    display flex
    justify-content center
    align-items center
  }
}
.price-inputs {
  display grid
  grid-template-columns 160px 1fr 1fr 160px 1fr
  gap 10px
}
</style>
